"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.suggestYouTubeHashtags = exports.validateYouTubeThumbnailDimensions = exports.validateYouTubeHashtagCount = exports.countYouTubeHashtags = exports.ensureShortsMetadata = exports.ensureShortsHashtag = exports.isYouTubeAuthError = exports.nextYouTubeQuotaEmailKind = exports.isYouTubeQuotaPersistent = exports.YOUTUBE_QUOTA_PERSISTENT_DAYS = exports.isYouTubeQuotaError = exports.youtubePostProgressLabel = exports.youtubeShortsUrl = exports.isPublishWithinSla = exports.publishDelaySeconds = exports.PUBLISH_SLA_SECONDS = exports.PUBLISH_MAX_RETRIES = exports.SCHEDULE_EDIT_LOCK_MINUTES = exports.SCHEDULE_MIN_LEAD_MINUTES = exports.validateYouTubeMedia = exports.validateYouTubeTitleInput = exports.hasYouTubeTitle = exports.resolveYouTubeVideoTitle = exports.youtubePrivacyLabel = exports.YOUTUBE_PRIVACY_LABELS = exports.YOUTUBE_PRIVACY_STATUSES = exports.YOUTUBE_ASPECT_TOLERANCE = exports.YOUTUBE_REQUIRED_ASPECT = exports.YOUTUBE_THUMBNAIL_DIMENSION_TOLERANCE = exports.YOUTUBE_THUMBNAIL_HEIGHT = exports.YOUTUBE_THUMBNAIL_WIDTH = exports.YOUTUBE_MAX_HASHTAGS = exports.YOUTUBE_DESCRIPTION_MAX = exports.YOUTUBE_TITLE_MAX = exports.YOUTUBE_MAX_DURATION_SEC = exports.YOUTUBE_MAX_VIDEO_BYTES = exports.YOUTUBE_VIDEO_EXTENSIONS = void 0;
const core_1 = require("@js-joda/core");
const HttpInterface_1 = require("./HttpInterface");
/** YouTube Shorts video limits for MVP. */
exports.YOUTUBE_VIDEO_EXTENSIONS = ["mp4", "mov"];
exports.YOUTUBE_MAX_VIDEO_BYTES = 256 * 1024 * 1024; // 256 MB
exports.YOUTUBE_MAX_DURATION_SEC = 60;
exports.YOUTUBE_TITLE_MAX = 100;
exports.YOUTUBE_DESCRIPTION_MAX = 5000;
exports.YOUTUBE_MAX_HASHTAGS = 15;
exports.YOUTUBE_THUMBNAIL_WIDTH = 1280;
exports.YOUTUBE_THUMBNAIL_HEIGHT = 720;
exports.YOUTUBE_THUMBNAIL_DIMENSION_TOLERANCE = 0.1;
/** Required vertical aspect ratio for Shorts (9:16). */
exports.YOUTUBE_REQUIRED_ASPECT = 9 / 16;
exports.YOUTUBE_ASPECT_TOLERANCE = 0.05;
/** YouTube Data API privacy_status values. */
exports.YOUTUBE_PRIVACY_STATUSES = [
    "public",
    "unlisted",
    "private",
];
exports.YOUTUBE_PRIVACY_LABELS = {
    public: "Public",
    unlisted: "Unlisted",
    private: "Private",
};
const youtubePrivacyLabel = (status) => { var _a; return (_a = exports.YOUTUBE_PRIVACY_LABELS[status]) !== null && _a !== void 0 ? _a : status; };
exports.youtubePrivacyLabel = youtubePrivacyLabel;
/** Build a valid YouTube video title from subject and/or description. */
const resolveYouTubeVideoTitle = (subject, content, fallback = "Short") => {
    var _a, _b;
    const fromSubject = subject === null || subject === void 0 ? void 0 : subject.trim();
    if (fromSubject) {
        return fromSubject.slice(0, exports.YOUTUBE_TITLE_MAX);
    }
    const trimmedContent = content === null || content === void 0 ? void 0 : content.trim();
    if (trimmedContent) {
        const firstLine = (_b = (_a = trimmedContent
            .split(/\r?\n/)
            .find((line) => line.trim())) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : trimmedContent;
        return firstLine.slice(0, exports.YOUTUBE_TITLE_MAX);
    }
    return fallback.slice(0, exports.YOUTUBE_TITLE_MAX);
};
exports.resolveYouTubeVideoTitle = resolveYouTubeVideoTitle;
/** Whether subject or post copy can produce a non-empty YouTube title. */
const hasYouTubeTitle = (subject, content) => {
    var _a;
    if (subject === null || subject === void 0 ? void 0 : subject.trim()) {
        return true;
    }
    const trimmedContent = content === null || content === void 0 ? void 0 : content.trim();
    if (!trimmedContent) {
        return false;
    }
    const firstLine = (_a = trimmedContent
        .split(/\r?\n/)
        .find((line) => line.trim())) === null || _a === void 0 ? void 0 : _a.trim();
    return !!firstLine;
};
exports.hasYouTubeTitle = hasYouTubeTitle;
const validateYouTubeTitleInput = (subject, content) => {
    if ((0, exports.hasYouTubeTitle)(subject, content)) {
        return { valid: true, errors: [] };
    }
    return {
        valid: false,
        errors: [
            "Short title is required. Enter a title in the Short title field (max 100 characters).",
        ],
    };
};
exports.validateYouTubeTitleInput = validateYouTubeTitleInput;
const normalizeExt = (extension) => (extension !== null && extension !== void 0 ? extension : "").toLowerCase().replace(/^\./, "");
const isVideoType = (file) => {
    const ext = normalizeExt(file.extension);
    if (file.type === HttpInterface_1.ContentType.VIDEO) {
        return true;
    }
    return exports.YOUTUBE_VIDEO_EXTENSIONS.includes(ext);
};
const formatDuration = (seconds) => {
    const total = Math.ceil(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    if (mins > 0) {
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    }
    return `${secs}`;
};
/**
 * Hard validation for YouTube Shorts media before schedule/publish.
 * Returns blocking errors (empty = valid enough to attempt publish).
 */
const validateYouTubeMedia = (files) => {
    const errors = [];
    const warnings = [];
    if (files.length === 0) {
        errors.push("YouTube Shorts requires a vertical video file.");
        return { valid: false, errors, warnings };
    }
    if (files.length > 1) {
        errors.push("YouTube Shorts allows only one video per post.");
    }
    const videos = files.filter(isVideoType);
    const nonVideos = files.filter((f) => !isVideoType(f));
    if (nonVideos.length > 0) {
        const names = nonVideos
            .map((f) => f.filename || normalizeExt(f.extension) || "file")
            .join(", ");
        errors.push(`YouTube Shorts requires a video file (${names} is not supported). Use MP4 or MOV.`);
    }
    for (const video of videos) {
        const ext = normalizeExt(video.extension);
        if (ext &&
            !exports.YOUTUBE_VIDEO_EXTENSIONS.includes(ext)) {
            errors.push(`YouTube Shorts video must be MP4 or MOV (got .${ext}).`);
        }
        if (video.size != null && video.size > exports.YOUTUBE_MAX_VIDEO_BYTES) {
            errors.push("YouTube Shorts video must be 256 MB or smaller.");
        }
        if (video.durationSec != null &&
            video.durationSec > exports.YOUTUBE_MAX_DURATION_SEC) {
            errors.push(`Your video is ${formatDuration(video.durationSec)} — Shorts must be 60 seconds or less.`);
        }
        if (video.width != null &&
            video.height != null &&
            video.width > 0 &&
            video.height > 0) {
            const ratio = video.width / video.height;
            if (Math.abs(ratio - exports.YOUTUBE_REQUIRED_ASPECT) > exports.YOUTUBE_ASPECT_TOLERANCE) {
                errors.push("Shorts require a vertical video (9:16). Please re-record or crop your video before uploading.");
            }
        }
    }
    if (videos.length > 0 && videos.every((v) => v.durationSec == null)) {
        warnings.push("Video duration could not be verified. Shorts must be 60 seconds or less.");
    }
    if (videos.length > 0 &&
        videos.every((v) => v.width == null || v.height == null)) {
        warnings.push("Video dimensions could not be verified. Shorts require a vertical 9:16 aspect ratio.");
    }
    return { valid: errors.length === 0, errors, warnings };
};
exports.validateYouTubeMedia = validateYouTubeMedia;
exports.SCHEDULE_MIN_LEAD_MINUTES = 15;
exports.SCHEDULE_EDIT_LOCK_MINUTES = 10;
exports.PUBLISH_MAX_RETRIES = 3;
/** PRD: scheduled publishes should execute within 5 minutes of scheduled time. */
exports.PUBLISH_SLA_SECONDS = 300;
const publishDelaySeconds = (scheduledAtIso, publishedAtIso) => core_1.ChronoUnit.SECONDS.between(core_1.Instant.parse(scheduledAtIso), core_1.Instant.parse(publishedAtIso));
exports.publishDelaySeconds = publishDelaySeconds;
const isPublishWithinSla = (delaySeconds) => delaySeconds <= exports.PUBLISH_SLA_SECONDS;
exports.isPublishWithinSla = isPublishWithinSla;
const youtubeShortsUrl = (videoId) => `https://www.youtube.com/shorts/${videoId}`;
exports.youtubeShortsUrl = youtubeShortsUrl;
/** User-facing label for a YouTube post that is scheduled or in progress. */
const youtubePostProgressLabel = (publishStatus, fallback) => {
    if (publishStatus === "retrying") {
        return "Retrying publish…";
    }
    return fallback !== null && fallback !== void 0 ? fallback : "Publishing…";
};
exports.youtubePostProgressLabel = youtubePostProgressLabel;
/** Detect YouTube API daily quota exhaustion from an error message. */
const isYouTubeQuotaError = (message) => /quota|exceeded.*limit|dailyLimitExceeded|rateLimitExceeded/i.test(message);
exports.isYouTubeQuotaError = isYouTubeQuotaError;
/** PRD: escalate messaging when quota failures persist 2+ days. */
exports.YOUTUBE_QUOTA_PERSISTENT_DAYS = 2;
/** True when quota retries for this Short started at least 2 calendar days ago. */
const isYouTubeQuotaPersistent = (startedAtIso, now = core_1.Instant.now()) => {
    if (!startedAtIso) {
        return false;
    }
    try {
        return (core_1.ChronoUnit.DAYS.between(core_1.Instant.parse(startedAtIso), now) >=
            exports.YOUTUBE_QUOTA_PERSISTENT_DAYS);
    }
    catch (_a) {
        return false;
    }
};
exports.isYouTubeQuotaPersistent = isYouTubeQuotaPersistent;
/**
 * Which quota email to send for this failure, or null if already notified
 * at this severity (initial once, then persistent once after 2+ days).
 */
const nextYouTubeQuotaEmailKind = (previousKind, persistent) => {
    if (persistent) {
        return previousKind === "persistent" ? null : "persistent";
    }
    return previousKind === "initial" || previousKind === "persistent"
        ? null
        : "initial";
};
exports.nextYouTubeQuotaEmailKind = nextYouTubeQuotaEmailKind;
/** Detect OAuth/token errors that require the user to reconnect YouTube. */
const isYouTubeAuthError = (message) => /invalid_grant|invalid_token|token.*expired|unauthorized|401|403|revoked|insufficient.*permission|not connected|login.?required|authorization/i.test(message);
exports.isYouTubeAuthError = isYouTubeAuthError;
const ensureShortsHashtag = (text) => {
    if (!text.includes("#Shorts")) {
        return `${text} #Shorts`;
    }
    return text;
};
exports.ensureShortsHashtag = ensureShortsHashtag;
/**
 * Ensure #Shorts appears on title or description (not both).
 * Prefers description so the title stays within the 100-char limit.
 */
const ensureShortsMetadata = (title, description) => {
    if (title.includes("#Shorts") || description.includes("#Shorts")) {
        return { title, description };
    }
    if (description.trim()) {
        return { title, description: (0, exports.ensureShortsHashtag)(description) };
    }
    return { title: (0, exports.ensureShortsHashtag)(title), description };
};
exports.ensureShortsMetadata = ensureShortsMetadata;
const YOUTUBE_HASHTAG_RULES = [
    { keywords: ["fitness", "workout", "gym", "exercise"], tag: "#Fitness" },
    { keywords: ["food", "recipe", "cooking", "kitchen"], tag: "#Food" },
    { keywords: ["travel", "vacation", "trip", "adventure"], tag: "#Travel" },
    { keywords: ["beauty", "makeup", "skincare", "fashion"], tag: "#Beauty" },
    { keywords: ["tech", "software", "coding", "ai"], tag: "#Tech" },
    { keywords: ["business", "entrepreneur", "startup"], tag: "#Business" },
    { keywords: ["comedy", "funny", "humor", "laugh"], tag: "#Comedy" },
    { keywords: ["music", "song", "dance", "singing"], tag: "#Music" },
    { keywords: ["gaming", "gameplay", "gamer"], tag: "#Gaming" },
    { keywords: ["tutorial", "howto", "tips", "learn"], tag: "#Tutorial" },
];
/** Count hashtag tokens in post copy (#word). */
const countYouTubeHashtags = (text) => {
    var _a;
    const matches = text.match(/#[\w]+/g);
    return (_a = matches === null || matches === void 0 ? void 0 : matches.length) !== null && _a !== void 0 ? _a : 0;
};
exports.countYouTubeHashtags = countYouTubeHashtags;
const validateYouTubeHashtagCount = (content) => {
    const count = (0, exports.countYouTubeHashtags)(content);
    if (count > exports.YOUTUBE_MAX_HASHTAGS) {
        return {
            valid: false,
            errors: [
                `You can add up to ${exports.YOUTUBE_MAX_HASHTAGS} hashtags (currently ${count}).`,
            ],
        };
    }
    return { valid: true, errors: [] };
};
exports.validateYouTubeHashtagCount = validateYouTubeHashtagCount;
const validateYouTubeThumbnailDimensions = (width, height) => {
    if (width <= 0 || height <= 0) {
        return {
            valid: false,
            errors: ["Could not read thumbnail dimensions. Use a JPG or PNG image."],
        };
    }
    const widthRatio = width / exports.YOUTUBE_THUMBNAIL_WIDTH;
    const heightRatio = height / exports.YOUTUBE_THUMBNAIL_HEIGHT;
    const ratioDiff = Math.abs(widthRatio - heightRatio);
    if (ratioDiff > exports.YOUTUBE_THUMBNAIL_DIMENSION_TOLERANCE ||
        width <
            exports.YOUTUBE_THUMBNAIL_WIDTH * (1 - exports.YOUTUBE_THUMBNAIL_DIMENSION_TOLERANCE) ||
        height <
            exports.YOUTUBE_THUMBNAIL_HEIGHT * (1 - exports.YOUTUBE_THUMBNAIL_DIMENSION_TOLERANCE)) {
        return {
            valid: false,
            errors: [
                `Thumbnail should be ${exports.YOUTUBE_THUMBNAIL_WIDTH}×${exports.YOUTUBE_THUMBNAIL_HEIGHT} (got ${width}×${height}).`,
            ],
        };
    }
    return { valid: true, errors: [] };
};
exports.validateYouTubeThumbnailDimensions = validateYouTubeThumbnailDimensions;
const hasHashtag = (text, tag) => {
    const normalized = tag.toLowerCase();
    return text.toLowerCase().includes(normalized);
};
/** Suggest Shorts hashtags from post copy and the client's business category. */
const suggestYouTubeHashtags = (content, businessCategory, limit = exports.YOUTUBE_MAX_HASHTAGS) => {
    const suggestions = [];
    const combined = `${content} ${businessCategory !== null && businessCategory !== void 0 ? businessCategory : ""}`.toLowerCase();
    if (!hasHashtag(content, "#Shorts")) {
        suggestions.push("#Shorts");
    }
    if (!hasHashtag(content, "#YouTubeShorts")) {
        suggestions.push("#YouTubeShorts");
    }
    for (const rule of YOUTUBE_HASHTAG_RULES) {
        if (suggestions.length >= limit) {
            break;
        }
        if (rule.keywords.some((keyword) => combined.includes(keyword)) &&
            !hasHashtag(content, rule.tag)) {
            suggestions.push(rule.tag);
        }
    }
    return suggestions.slice(0, limit);
};
exports.suggestYouTubeHashtags = suggestYouTubeHashtags;
